// Every legal 3-half-move opening of a ballot variant, and why the F.I.D. table
// does or does not list it.
//
// Pure TypeScript over @draughts/core — no React, no DOM — so the rules page can
// render it inside the Node prerender exactly as it does in the browser.
//
// The board itself decides the first three columns of the answer: there are 302
// legal sequences, they reach only 216 distinct positions, and the general table
// holds 174 of those positions (one sequence each, never two for the same one).
// What is left over is either a transposition of a listed opening or a position
// the federation chose not to use.

import { Color, GameVariant, ITALIAN_BALLOT, ENGLISH_BALLOT, type BallotOpening } from '@draughts/shared';
import { createInitialBoard, getAllLegalMoves, applyMove, boardStateToFen } from '@draughts/core';

/**
 * Why an opening is, or is not, in the official table:
 * - `ballot`        — listed in the general table.
 * - `transposition` — a different move order reaching a position the table
 *                     already lists under another number.
 * - `material`      — one side comes out a man up and the opponent has no
 *                     recapture: the regulation drops these as "immediate loss
 *                     of a piece". Two per variant.
 * - `unlisted`      — a position the table does not cover at all. No mechanical
 *                     reason: it is a technical choice of the federation.
 */
export type OpeningStatus = 'ballot' | 'transposition' | 'material' | 'unlisted';

export interface EnumeratedOpening {
  /** 1-based index in enumeration order (by first, then second, then third move). */
  n: number;
  /** The 3 half-moves, `from-to` or `fromxto` for a capture. */
  moves: string[];
  /** Position after the three half-moves — the key transpositions collapse on. */
  fen: string;
  status: OpeningStatus;
  /** The official entry, when this sequence is in the table. */
  ballot: BallotOpening | null;
  /** For a transposition: the general-table number reaching the same position. */
  sameAs: number | null;
  /** True when the third half-move is a forced capture. */
  capture: boolean;
}

const ballotOf = (variant: GameVariant): readonly BallotOpening[] =>
  variant === GameVariant.ENGLISH ? ENGLISH_BALLOT : ITALIAN_BALLOT;

/** Ballot notation writes captures with `-` as often as with `x`; compare on squares. */
const key = (moves: readonly string[]): string => moves.map((m) => m.replace('x', '-')).join('|');

const cache = new Map<GameVariant, EnumeratedOpening[]>();

/**
 * All legal 3-half-move openings of a ballot variant, classified. Cached per
 * variant: the prerender renders the same page once per language.
 */
export function enumerateOpenings(variant: GameVariant): EnumeratedOpening[] {
  const cached = cache.get(variant);
  if (cached) return cached;

  const listed = new Map(ballotOf(variant).map((o) => [key(o.moves), o]));
  const rows: Array<Omit<EnumeratedOpening, 'status' | 'sameAs'> & { balance: number; recapture: boolean }> = [];

  const start = createInitialBoard(variant);
  let n = 0;
  for (const m1 of getAllLegalMoves(start)) {
    const after1 = applyMove(start, m1);
    for (const m2 of getAllLegalMoves(after1)) {
      const after2 = applyMove(after1, m2);
      for (const m3 of getAllLegalMoves(after2)) {
        const after3 = applyMove(after2, m3);
        const moves = [m1, m2, m3].map((m) => `${m.from}${m.captures?.length ? 'x' : '-'}${m.to}`);
        // Compare against the enum, never the string: a literal that stops
        // matching would silently count 0 for both sides and read as "even".
        const white = after3.pieces.filter((p) => p.color === Color.WHITE).length;
        const black = after3.pieces.filter((p) => p.color === Color.BLACK).length;
        rows.push({
          n: ++n,
          moves,
          fen: boardStateToFen(after3),
          ballot: listed.get(key(moves)) ?? null,
          capture: Boolean(m3.captures?.length),
          balance: white - black,
          recapture: getAllLegalMoves(after3).some((m) => m.captures?.length),
        });
      }
    }
  }

  // A position is "covered" as soon as one sequence reaching it is in the table.
  const coveredBy = new Map<string, number>();
  for (const row of rows) if (row.ballot) coveredBy.set(row.fen, row.ballot.id);

  const result = rows.map((row): EnumeratedOpening => {
    const { balance, recapture, ...rest } = row;
    if (row.ballot) return { ...rest, status: 'ballot', sameAs: null };
    const twin = coveredBy.get(row.fen);
    if (twin !== undefined) return { ...rest, status: 'transposition', sameAs: twin };
    // A man up with nothing to take back: the game is decided before it starts.
    if (balance !== 0 && !recapture) return { ...rest, status: 'material', sameAs: null };
    return { ...rest, status: 'unlisted', sameAs: null };
  });

  cache.set(variant, result);
  return result;
}

/** How many openings fall in each status — the numbers the page explains. */
export function openingStatusCounts(openings: readonly EnumeratedOpening[]): Record<OpeningStatus, number> {
  const counts: Record<OpeningStatus, number> = { ballot: 0, transposition: 0, material: 0, unlisted: 0 };
  for (const o of openings) counts[o.status] += 1;
  return counts;
}

/** Distinct positions the openings reach — 216 of 302 on both ballot variants. */
export function distinctPositions(openings: readonly EnumeratedOpening[]): number {
  return new Set(openings.map((o) => o.fen)).size;
}
